import { useEffect, useState } from "react";
import {
  Heart,
  MessageCircle,
  Repeat2,
  Eye,
  MousePointerClick,
} from "lucide-react";
import {
  api,
  type AnalyticsOverview,
  type DailyActivity,
  type CampaignSummary,
  type EngagementByPlatform,
} from "../../lib/api";
import MetricCard from "./MetricCard";
import ActivityChart from "./ActivityChart";
import CampaignBreakdownChart from "./CampaignBreakdownChart";

const PLAN_LIMIT: Record<string, number> = {
  free: 5,
  solo: 30,
  business: 100,
  agency: Infinity,
};

export default function Analytics() {
  const [overview, setOverview] = useState<AnalyticsOverview | null>(null);
  const [activity, setActivity] = useState<DailyActivity[]>([]);
  const [campaigns, setCampaigns] = useState<CampaignSummary[]>([]);
  const [engByPlatform, setEngByPlatform] = useState<EngagementByPlatform[]>(
    [],
  );
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.analytics.overview(),
      api.analytics.activity(30),
      api.analytics.campaigns(),
      api.analytics
        .engagement()
        .catch(() => ({ topPosts: [], byPlatform: [] })),
    ])
      .then(([ov, act, camp, eng]) => {
        setOverview(ov);
        setActivity(act.activity);
        setCampaigns(camp.campaigns);
        setEngByPlatform(eng.byPlatform);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <p className="text-faint text-sm">Loading...</p>;
  if (!overview)
    return <p className="text-red-600 text-sm">Failed to load analytics.</p>;

  const {
    postsGenerated,
    postsApproved,
    postsRejected,
    postsPublished,
    approvalRate,
    cycleUsage,
  } = overview;
  const limit = PLAN_LIMIT[cycleUsage.plan] ?? 5;
  const pct =
    limit === Infinity ? 0 : Math.min((cycleUsage.used / limit) * 100, 100);

  return (
    <div>
      <div className="mb-8">
        <h1 className="page-title">Analytics</h1>
        <p className="text-sm text-muted mt-1">
          Content performance and campaign metrics.
        </p>
      </div>

      {/* Metric grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 mb-6">
        <MetricCard label="Posts generated" value={postsGenerated} />
        <MetricCard label="Posts approved" value={postsApproved} />
        <MetricCard label="Posts rejected" value={postsRejected} />
        <MetricCard label="Posts published" value={postsPublished} />
        <MetricCard
          label="Approval rate"
          value={`${Math.round(approvalRate * 100)}%`}
          sub={approvalRate >= 0.7 ? "On target" : "Below 70% target"}
        />
      </div>

      {/* Cycle usage */}
      <div className="card p-6 mb-6">
        <div className="flex flex-wrap items-center justify-between gap-x-3 mb-2">
          <p className="font-semibold text-ink capitalize">
            {cycleUsage.plan} plan
          </p>
          <p className="text-sm text-muted">
            {limit === Infinity
              ? "Unlimited posts"
              : `${cycleUsage.used} / ${limit} posts this cycle`}
          </p>
        </div>
        {limit !== Infinity && (
          <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full transition-all ${pct > 85 ? "bg-red-500" : pct > 60 ? "bg-amber-400" : "bg-brand-500"}`}
              style={{ width: `${pct}%` }}
            />
          </div>
        )}
        {cycleUsage.periodEnd && (
          <p className="text-xs text-faint mt-2">
            Resets{" "}
            {new Date(cycleUsage.periodEnd).toLocaleDateString("en-US", {
              month: "long",
              day: "numeric",
            })}
          </p>
        )}
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <ActivityChart data={activity} />
        <CampaignBreakdownChart campaigns={campaigns} />
      </div>

      {/* Engagement metrics */}
      {overview.engagement &&
        (overview.engagement.likes > 0 ||
          overview.engagement.impressions > 0) && (
          <div className="mb-6">
            <h2 className="eyebrow mb-3">Platform Engagement</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 mb-4">
              <div className="card p-5 flex items-center gap-3">
                <Heart className="h-5 w-5 text-brand-500 flex-shrink-0" />
                <div>
                  <p className="text-xs text-faint">Likes</p>
                  <p className="text-lg font-bold text-ink">
                    {overview.engagement.likes.toLocaleString()}
                  </p>
                </div>
              </div>
              <div className="card p-5 flex items-center gap-3">
                <MessageCircle className="h-5 w-5 text-brand-500 flex-shrink-0" />
                <div>
                  <p className="text-xs text-faint">Comments</p>
                  <p className="text-lg font-bold text-ink">
                    {overview.engagement.comments.toLocaleString()}
                  </p>
                </div>
              </div>
              <div className="card p-5 flex items-center gap-3">
                <Repeat2 className="h-5 w-5 text-brand-500 flex-shrink-0" />
                <div>
                  <p className="text-xs text-faint">Shares</p>
                  <p className="text-lg font-bold text-ink">
                    {overview.engagement.shares.toLocaleString()}
                  </p>
                </div>
              </div>
              <div className="card p-5 flex items-center gap-3">
                <Eye className="h-5 w-5 text-brand-500 flex-shrink-0" />
                <div>
                  <p className="text-xs text-faint">Impressions</p>
                  <p className="text-lg font-bold text-ink">
                    {overview.engagement.impressions.toLocaleString()}
                  </p>
                </div>
              </div>
              <div className="card p-5 flex items-center gap-3">
                <MousePointerClick className="h-5 w-5 text-brand-500 flex-shrink-0" />
                <div>
                  <p className="text-xs text-faint">Clicks</p>
                  <p className="text-lg font-bold text-ink">
                    {overview.engagement.clicks.toLocaleString()}
                  </p>
                </div>
              </div>
            </div>

            {/* Per-platform engagement breakdown */}
            {engByPlatform.length > 0 && (
              <div className="card overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-100">
                        <th className="text-left font-medium text-muted px-6 py-3">
                          Platform
                        </th>
                        <th className="text-right font-medium text-muted px-6 py-3">
                          Likes
                        </th>
                        <th className="text-right font-medium text-muted px-6 py-3">
                          Comments
                        </th>
                        <th className="text-right font-medium text-muted px-6 py-3">
                          Shares
                        </th>
                        <th className="text-right font-medium text-muted px-6 py-3">
                          Impressions
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {engByPlatform.map((ep) => (
                        <tr
                          key={ep.platform}
                          className="border-b border-gray-50"
                        >
                          <td className="px-6 py-3 font-medium text-ink capitalize">
                            {ep.platform.replace("_", " ")}
                          </td>
                          <td className="px-6 py-3 text-right text-slate">
                            {ep.likes.toLocaleString()}
                          </td>
                          <td className="px-6 py-3 text-right text-slate">
                            {ep.comments.toLocaleString()}
                          </td>
                          <td className="px-6 py-3 text-right text-slate">
                            {ep.shares.toLocaleString()}
                          </td>
                          <td className="px-6 py-3 text-right text-slate">
                            {ep.impressions.toLocaleString()}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

      {/* Campaign table */}
      {campaigns.length > 0 && (
        <div className="card overflow-hidden">
          <div className="p-6 pb-3">
            <h2 className="section-title">Campaigns</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-t border-b border-gray-100">
                  <th className="text-left font-medium text-muted px-6 py-3">
                    Campaign
                  </th>
                  <th className="text-right font-medium text-muted px-6 py-3">
                    Total
                  </th>
                  <th className="text-right font-medium text-muted px-6 py-3">
                    Approved
                  </th>
                  <th className="text-right font-medium text-muted px-6 py-3">
                    Published
                  </th>
                  <th className="text-right font-medium text-muted px-6 py-3">
                    Approval %
                  </th>
                  <th className="text-left font-medium text-muted px-6 py-3">
                    Platforms
                  </th>
                </tr>
              </thead>
              <tbody>
                {campaigns.map((c) => (
                  <tr
                    key={c.campaignId}
                    className="border-b border-gray-50 hover:bg-gray-50 cursor-pointer"
                    onClick={() =>
                      (window.location.href = `/analytics/campaigns/${c.campaignId}`)
                    }
                  >
                    <td className="px-6 py-3 font-medium text-ink">{c.name}</td>
                    <td className="px-6 py-3 text-right text-slate">
                      {c.total}
                    </td>
                    <td className="px-6 py-3 text-right text-slate">
                      {c.approved}
                    </td>
                    <td className="px-6 py-3 text-right text-slate">
                      {c.published}
                    </td>
                    <td className="px-6 py-3 text-right text-slate">
                      {Math.round(c.approvalRate * 100)}%
                    </td>
                    <td className="px-6 py-3">
                      <div className="flex gap-1 flex-wrap">
                        {c.platforms.map((p) => (
                          <span
                            key={p}
                            className="text-xs bg-gray-100 text-slate px-2 py-0.5 rounded-full"
                          >
                            {p.replace("_", " ")}
                          </span>
                        ))}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
